import { Chip, makeStyles } from "@material-ui/core";
import React, { useContext } from "react";

import { RoomEntryState } from "../../../../enums/roomEntryState";
import { useForceRerender } from "../../../../hooks/useForceRerender";
import SignalRContext from "../../../../signalr/context";

const useStyles = makeStyles({
    chip: {
        width: "200px",
        height: "auto",
        padding: "8px",
    },
    chipLabel: {
        whiteSpace: "normal",
        textAlign: "center",
    },
});

interface Props {
    siteId: number;
}

export function SiteRoomStatusIndicator({ siteId }: Props) {
    // The `roomState` map reference does not change when new data is received,
    // so this component will not re-render automatically.
    useForceRerender(2000);

    const { roomState } = useContext(SignalRContext);
    const siteRoomState = roomState.get(siteId);

    const classes = useStyles();

    if (!siteRoomState) {
        return (
            <Chip
                color="default"
                label={
                    <div className={classes.chipLabel}>
                        Unknown state
                    </div>
                }
                className={classes.chip}
            />
        );
    }

    const { roomEntryState, customerName } = siteRoomState;

    return (
        <Chip
            color={getIndicatorColor(roomEntryState)}
            label={
                <div className={classes.chipLabel}>
                    {getIndicatorLabel(roomEntryState, customerName)}
                </div>
            }
            className={classes.chip}
        />
    );
}

function getIndicatorColor(roomEntryState: RoomEntryState) {
    switch (roomEntryState) {
        case RoomEntryState.EnteringRoom:
        case RoomEntryState.EnteringRoomDoorOpen:
        case RoomEntryState.InRoom:
        case RoomEntryState.ExitingRoom:
            return "primary";
        case RoomEntryState.Unauthorized:
        case RoomEntryState.BoxLeftOpen:
            return "secondary";
        case RoomEntryState.Idle:
        default:
            return "default";
    }
}

function getIndicatorLabel(
    roomEntryState: RoomEntryState,
    customerName = "unknown customer",
) {
    switch (roomEntryState) {
        case RoomEntryState.EnteringRoom:
            return `${customerName} entering room.`;
        case RoomEntryState.EnteringRoomDoorOpen:
            return `${customerName} entering room, door is open.`;
        case RoomEntryState.InRoom:
            return `Room occupied by ${customerName}`;
        case RoomEntryState.ExitingRoom:
            return `${customerName} exiting room.`;
        case RoomEntryState.Unauthorized:
            return "Unauthorized activity";
        case RoomEntryState.BoxLeftOpen:
            return "Box Left Open";
        case RoomEntryState.Idle:
        default:
            return "Idle";
    }
}
